<template>
  <!-- Hidden when there's no active batch or the user dismissed it.
       Mounted globally in App.vue, so the toast stays visible while
       the user navigates between modules during a long export. -->
  <transition name="ept-fade">
    <div v-if="visible"
         class="ept-toast"
         :class="{
           'ept-toast-done':   batch.done && !batch.failed,
           'ept-toast-mixed':  batch.done && batch.failed > 0 && batch.completed > 0,
           'ept-toast-failed': batch.done && batch.completed === 0 && batch.failed > 0,
         }">

      <!-- Compact head: title + dismiss -->
      <div class="ept-head">
        <span class="ept-title">
          <span v-if="!batch.done" class="ept-spinner"></span>
          <i v-else-if="!batch.failed" class="fas fa-check-circle ept-ico-ok"></i>
          <i v-else class="fas fa-exclamation-triangle ept-ico-warn"></i>
          <span class="ept-label">{{ headline }}</span>
        </span>
        <button type="button"
                class="ept-dismiss"
                v-if="batch.done"
                @click="dismiss"
                aria-label="Dismiss">×</button>
      </div>

      <!-- Progress bar -->
      <div class="ept-bar">
        <div class="ept-fill" :style="{ width: percent + '%' }"></div>
      </div>

      <!-- Counts row -->
      <div class="ept-counts">
        <strong>{{ doneSoFar }}</strong> / <strong>{{ batch.total || '?' }}</strong>
        <span class="ept-pct">({{ percent }}%)</span>
        <span v-if="batch.failed" class="ept-fail">·  {{ batch.failed }} failed</span>
      </div>

      <!-- Expand for recent failures -->
      <details v-if="batch.recentFailures && batch.recentFailures.length"
               class="ept-failures">
        <summary>Recent failures ({{ batch.recentFailures.length }})</summary>
        <ul>
          <li v-for="(f, i) in batch.recentFailures" :key="i">
            <strong>{{ f.displayName || (f.personType + ' #' + (f.displayId || '?')) }}</strong>
            <span v-if="f.deviceName"> on {{ f.deviceName }}</span>
            <span v-if="f.error" class="ept-fail-msg"> — {{ f.error }}</span>
          </li>
        </ul>
      </details>
    </div>
  </transition>
</template>

<script>
/**
 * Global toast that surfaces the active export/remove batch wherever
 * the user is in the app. Reads from the {@code exportProgress} Vuex
 * module which holds the polling state.
 *
 * <h3>Module registration</h3>
 * The store module is registered in {@code App.vue}'s
 * {@code beforeCreate} hook so it's guaranteed to exist before this
 * toast's computed properties evaluate. Don't duplicate the
 * registration here — it would race with App.vue and the
 * {@code preserveState:false} default of registerModule could wipe
 * an active batch on a stray re-mount.
 *
 * <h3>Auto-dismiss on success</h3>
 * On a fully-successful batch the toast auto-fades after a short
 * window so the user isn't stuck dismissing every push. Mixed/failed
 * batches stay until manually dismissed — those need attention.
 */
export default {
  name: 'ExportProgressToast',

  data() {
    return {
      autoDismissTimer: null,
    };
  },

  computed: {
    visible() {
      // Module registers in App.vue's beforeCreate, but guard anyway:
      // a hot-reload could briefly re-mount before the new module is
      // present, and falling back to false is the right default.
      if (!this.$store.state.exportProgress) return false;
      return this.$store.getters['exportProgress/isVisible'];
    },
    batch() {
      const ep = this.$store.state.exportProgress;
      return (ep && ep.batch) || {};
    },
    percent() {
      if (!this.$store.state.exportProgress) return 0;
      return this.$store.getters['exportProgress/percent'];
    },
    doneSoFar() {
      return (this.batch.completed || 0) + (this.batch.failed || 0);
    },
    headline() {
      const b = this.batch;
      if (!b.done) return b.label || 'Working…';
      if (!b.failed) {
        return b.kind === 'remove' ? 'Removal complete' : 'Send complete';
      }
      if (b.completed > 0) {
        return `Finished with ${b.failed} failure${b.failed === 1 ? '' : 's'}`;
      }
      return `All ${b.failed} operation${b.failed === 1 ? '' : 's'} failed`;
    },
  },

  watch: {
    /**
     * When a batch completes cleanly, schedule an auto-dismiss after
     * 8s. Mixed/failed batches stay sticky — the user must X them.
     */
    'batch.done'(isDone) {
      if (!isDone) return;
      if (this.batch.failed && this.batch.failed > 0) return;
      if (this.autoDismissTimer) clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = setTimeout(() => {
        this.$store.dispatch('exportProgress/dismiss');
      }, 8000);
    },
  },

  methods: {
    dismiss() {
      if (this.autoDismissTimer) clearTimeout(this.autoDismissTimer);
      this.$store.dispatch('exportProgress/dismiss');
    },
  },

  beforeUnmount() {
    if (this.autoDismissTimer) clearTimeout(this.autoDismissTimer);
  },
};
</script>

<style scoped>
/* Fixed bottom-right anchor — distinct from the top-right toast stack
   used by status notifications elsewhere in the app, so this is
   visually a *worker* not a one-shot notification. */
.ept-toast {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 320px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border: 1px solid var(--att-border, #e3e8ef);
  border-left: 4px solid var(--att-primary, #e91e63);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(15,23,42,0.18);
  padding: 0.75rem 0.9rem;
  z-index: 1090;
  font-size: 0.82rem;
  color: #1a2332;
}

.ept-toast-done   { border-left-color: #10b981; }
.ept-toast-mixed  { border-left-color: #f59e0b; }
.ept-toast-failed { border-left-color: #ef4444; }

.ept-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.ept-title {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-weight: 600;
  font-size: 0.83rem;
  line-height: 1.3;
}
.ept-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 230px;
}
.ept-dismiss {
  background: transparent;
  border: 0;
  color: #64748b;
  font-size: 1.05rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 0.25rem;
}
.ept-dismiss:hover { color: #1a2332; }

.ept-spinner {
  width: 13px;
  height: 13px;
  border: 2px solid rgba(233,30,99,0.25);
  border-top-color: var(--att-primary, #e91e63);
  border-radius: 50%;
  animation: ept-spin 0.7s linear infinite;
  display: inline-block;
}
.ept-ico-ok    { color: #10b981; }
.ept-ico-warn  { color: #f59e0b; }

.ept-bar {
  width: 100%;
  height: 6px;
  background: #f1f4f9;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 0.4rem;
}
.ept-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--att-primary, #e91e63), var(--att-primary-700, #c2185b));
  border-radius: 999px;
  transition: width 250ms ease-out;
}
.ept-toast-done   .ept-fill { background: linear-gradient(90deg, #10b981, #059669); }
.ept-toast-mixed  .ept-fill { background: linear-gradient(90deg, #f59e0b, #d97706); }
.ept-toast-failed .ept-fill { background: linear-gradient(90deg, #ef4444, #b91c1c); }

.ept-counts {
  font-size: 0.75rem;
  color: #4a5568;
}
.ept-pct  { color: #8492a6; margin-left: 0.25rem; }
.ept-fail { color: #b91c1c; font-weight: 600; margin-left: 0.4rem; }

.ept-failures {
  margin-top: 0.5rem;
  font-size: 0.74rem;
}
.ept-failures summary {
  cursor: pointer;
  color: #b91c1c;
  font-weight: 600;
}
.ept-failures ul {
  list-style: none;
  padding: 0.4rem 0 0;
  margin: 0;
  max-height: 130px;
  overflow-y: auto;
}
.ept-failures li {
  padding: 0.2rem 0;
  border-top: 1px solid #f1f4f9;
}
.ept-failures li:first-child { border-top: 0; }
.ept-fail-msg { color: #6b7280; }

@keyframes ept-spin { to { transform: rotate(360deg); } }

.ept-fade-enter-active, .ept-fade-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}
.ept-fade-enter-from, .ept-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* Dark mode — picks up the global theme via the :data-theme attribute
   on the App wrapper. Scoped style with deep-context selector matches. */
:global([data-theme="dark"]) .ept-toast {
  background: #1f2937;
  color: #e5e7eb;
  border-color: rgba(255,255,255,0.08);
}
:global([data-theme="dark"]) .ept-bar { background: #374151; }
:global([data-theme="dark"]) .ept-failures li { border-top-color: #374151; }
</style>
